import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../components/ui/accordion";
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  MessageCircle, 
  HelpCircle, 
  Search, 
  Zap, 
  Shield, 
  Clock,
  CheckCircle,
  ExternalLink,
  ChevronRight,
  Sparkles
} from "lucide-react";

type ContactCardProps = {
  icon: ReactNode;
  title: string;
  subtitle: string;
  color: string;
  description: string;
};

type FeatureCardProps = {
  icon: ReactNode;
  title: string;
  description: string;
  color: string;
};

export default function HelpPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSection, setActiveSection] = useState("faq");

  const filteredFAQ = faqData.filter(item => 
    item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.a.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 font-sans text-slate-900 dark:text-white overflow-hidden transition-colors duration-300">
      {/* Enhanced Background Elements */}
      <div className="absolute top-[-20%] left-[-15%] w-96 h-96 sm:w-[500px] sm:h-[500px] rounded-full blur-3xl opacity-30 bg-gradient-to-br from-indigo-200/60 to-blue-200/60 dark:from-indigo-900/60 dark:to-blue-900/60 animate-pulse" />
      <div className="absolute bottom-[-25%] right-[-20%] w-96 h-96 sm:w-[600px] sm:h-[600px] rounded-full blur-3xl opacity-30 bg-gradient-to-br from-purple-200/60 to-pink-200/60 dark:from-purple-900/60 dark:to-pink-900/60 animate-pulse" />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 sm:w-48 sm:h-48 rounded-full blur-2xl opacity-20 bg-gradient-to-br from-cyan-200/50 to-teal-200/50 dark:from-cyan-900/50 dark:to-teal-900/50" />
      
      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/30 dark:bg-blue-400/30 rounded-full animate-pulse"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="container relative z-10 mx-auto px-4 py-6 sm:py-12 max-w-7xl">
        {/* Enhanced Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Button
  variant="outline"
  size="sm"
  onClick={() => window.history.back()}
  className="group self-start p-2 rounded-full 
             bg-white/60 dark:bg-slate-800/60 
             border border-slate-300/50 dark:border-slate-700/50 
             text-slate-700 dark:text-slate-300 
             hover:bg-slate-100/70 dark:hover:bg-slate-700/70 
             hover:text-slate-900 dark:hover:text-white 
             hover:border-blue-400/50 
             transition-all duration-300 
             hover:shadow-lg hover:shadow-blue-500/20 
             backdrop-blur-md"
>
  <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
</Button>

            <div className="flex items-center gap-4">
              <div className="relative p-3 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 dark:from-blue-500/30 dark:to-indigo-500/30 rounded-xl border border-blue-400/20 backdrop-blur-sm">
                <HelpCircle className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 dark:bg-green-400 rounded-full animate-ping" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 dark:bg-green-400 rounded-full" />
              </div>
              <div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-400 dark:via-indigo-400 dark:to-purple-400 bg-clip-text text-transparent leading-tight">
                  Help & Support
                </h1>
                <p className="text-slate-600 dark:text-slate-400 text-lg sm:text-xl mt-2 flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  Get assistance with CredexAI
                </p>
              </div>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="relative w-full lg:w-96">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-500 dark:text-slate-400" />
            <input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/50 dark:bg-slate-800/50 border border-slate-300/50 dark:border-slate-700/50 rounded-xl text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-400/50 transition-all duration-300 backdrop-blur-sm"
            />
          </div>
        </div>

        {/* Quick Access Features */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 p-2 bg-white/30 dark:bg-slate-800/30 rounded-xl backdrop-blur-sm border border-slate-300/30 dark:border-slate-700/30">
          {["faq", "contact", "guide"].map((section) => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 capitalize ${
                activeSection === section
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/25"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-700/50"
              }`}
            >
              {section === "faq" ? "FAQ" : section === "contact" ? "Contact" : "Getting Started"}
            </button>
          ))}
        </div>

        <div className="space-y-8">
          {/* FAQ Section */}
          {activeSection === "faq" && (
            <Card className="rounded-2xl bg-white/40 dark:bg-slate-800/40 border-slate-300/50 dark:border-slate-700/50 backdrop-blur-xl shadow-2xl transition-all duration-500">
              <CardHeader className="pb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <HelpCircle className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
                    Frequently Asked Questions
                  </CardTitle>
                </div>
                <CardDescription className="text-slate-600 dark:text-slate-400 text-lg">
                  {searchQuery ? `${filteredFAQ.length} results found` : "Find answers to common questions about using CredexAI"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full space-y-3">
                  {filteredFAQ.map((item, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${index + 1}`} 
                      className="border border-slate-300/30 dark:border-slate-700/30 rounded-xl bg-white/50 dark:bg-slate-800/20 overflow-hidden backdrop-blur-sm hover:border-blue-400/30 transition-all duration-300"
                    >
                      <AccordionTrigger className="text-slate-700 dark:text-slate-200 font-medium hover:text-slate-900 dark:hover:text-white px-6 py-4 text-left hover:no-underline group">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full opacity-60 group-hover:opacity-100 transition-opacity" />
                          {item.q}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 dark:text-slate-400 px-6 pb-4 leading-relaxed">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                {filteredFAQ.length === 0 && (
                  <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                    <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No results found for "{searchQuery}"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Contact Support */}
          {activeSection === "contact" && (
            <Card className="rounded-2xl bg-white/40 dark:bg-slate-800/40 border-slate-300/50 dark:border-slate-700/50 backdrop-blur-xl shadow-2xl transition-all duration-500">
              <CardHeader className="pb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-500/20 rounded-lg">
                    <MessageCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
                    Contact Support
                  </CardTitle>
                </div>
                <CardDescription className="text-slate-600 dark:text-slate-400 text-lg">
                  Need additional help? Our support team is here to assist you 24/7
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {contactMethods.map((method, index) => (
                    <ContactCard key={index} {...method} />
                  ))}
                </div>

                {/* Support Hours */}
                <div className="bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 border border-blue-300 dark:border-blue-700/30">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Support Hours</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-slate-700 dark:text-slate-300">
                    <div>
                      <p className="font-medium">Monday - Friday</p>
                      <p className="text-slate-600 dark:text-slate-400">9:00 AM - 8:00 PM EST</p>
                    </div>
                    <div>
                      <p className="font-medium">Saturday - Sunday</p>
                      <p className="text-slate-600 dark:text-slate-400">10:00 AM - 6:00 PM EST</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-100 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-300 dark:border-amber-700/30">
                  <div className="flex items-start gap-3">
                    <Shield className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-200 mb-2">Enterprise Support</p>
                      <p className="text-amber-700 dark:text-amber-300/80 leading-relaxed">
                        For enterprise customers, dedicated support and priority response times are available. 
                        Contact your account manager for more details.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Getting Started Guide */}
          {activeSection === "guide" && (
            <Card className="rounded-2xl bg-white/40 dark:bg-slate-800/40 border-slate-300/50 dark:border-slate-700/50 backdrop-blur-xl shadow-2xl transition-all duration-500">
              <CardHeader className="pb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Zap className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
                    Getting Started Guide
                  </CardTitle>
                </div>
                <CardDescription className="text-slate-600 dark:text-slate-400 text-lg">
                  Complete walkthrough to get you up and running with CredexAI
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {steps.map((step, i) => (
                  <div key={i} className="group relative">
                    <div className="flex gap-6 items-start p-6 rounded-xl bg-white/50 dark:bg-slate-800/30 border border-slate-300/30 dark:border-slate-700/30 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/70 dark:hover:bg-slate-800/50">
                      <div className="flex-shrink-0 w-12 h-12 text-white rounded-full flex items-center justify-center font-bold text-lg bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-all duration-300">
                        {i + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-bold text-xl text-slate-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">{step.title}</h4>
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                        </div>
                        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-lg">{step.desc}</p>
                        {step.tip && (
                          <div className="mt-3 p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg border border-blue-300 dark:border-blue-700/30">
                            <p className="text-blue-800 dark:text-blue-300 text-sm font-medium">💡 Pro Tip: {step.tip}</p>
                          </div>
                        )}
                      </div>
                      <ChevronRight className="h-6 w-6 text-slate-400 dark:text-slate-600 group-hover:text-blue-600 dark:group-hover:text-blue-400 group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                    {i < steps.length - 1 && (
                      <div className="w-0.5 h-6 bg-gradient-to-b from-slate-400 to-transparent dark:from-slate-600 dark:to-transparent ml-12 mt-2" />
                    )}
                  </div>
                ))}

                {/* Additional Resources */}
                <div className="mt-12 p-6 bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-xl border border-purple-300 dark:border-purple-700/30">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                    <ExternalLink className="h-5 w-5" />
                    Additional Resources
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <a href="#" className="flex items-center gap-2 text-purple-700 hover:text-purple-800 dark:text-purple-300 dark:hover:text-purple-200 transition-colors">
                      <ChevronRight className="h-4 w-4" />
                      Video Tutorials
                    </a>
                    <a href="#" className="flex items-center gap-2 text-purple-700 hover:text-purple-800 dark:text-purple-300 dark:hover:text-purple-200 transition-colors">
                      <ChevronRight className="h-4 w-4" />
                      API Documentation
                    </a>
                    <a href="#" className="flex items-center gap-2 text-purple-700 hover:text-purple-800 dark:text-purple-300 dark:hover:text-purple-200 transition-colors">
                      <ChevronRight className="h-4 w-4" />
                      Best Practices
                    </a>
                    <a href="#" className="flex items-center gap-2 text-purple-700 hover:text-purple-800 dark:text-purple-300 dark:hover:text-purple-200 transition-colors">
                      <ChevronRight className="h-4 w-4" />
                      Community Forum
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactCard({ icon, title, subtitle, color, description }: ContactCardProps) {
  return (
    <div
      className="group relative overflow-hidden rounded-xl p-6 transition-all duration-300 hover:scale-105 cursor-pointer backdrop-blur-sm border"
      style={{
        background: `linear-gradient(135deg, ${color}15, ${color}05)`,
        borderColor: `${color}30`,
      }}
    >
      <div className="flex items-center gap-4 mb-4">
        <div 
          className="p-3 rounded-xl shadow-lg transition-all duration-300 group-hover:scale-110"
          style={{ 
            background: `${color}20`,
            color: color,
            boxShadow: `0 4px 20px ${color}25`
          }}
        >
          {icon}
        </div>
        <div>
          <h3 className="font-bold text-slate-900 dark:text-white text-lg">{title}</h3>
          <p className="text-slate-600 dark:text-slate-400 font-medium">{subtitle}</p>
        </div>
      </div>
      <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">{description}</p>
      <div className="absolute top-0 right-0 w-20 h-20 opacity-10 group-hover:opacity-20 transition-all duration-300">
        <div 
          className="w-full h-full rounded-full blur-xl"
          style={{ background: color }}
        />
      </div>
    </div>
  );
}

function FeatureCard({ icon, title, description, color }: FeatureCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-xl p-6 bg-white/30 dark:bg-slate-800/30 border border-slate-300/30 dark:border-slate-700/30 hover:border-blue-400/30 transition-all duration-300 hover:bg-white/50 dark:hover:bg-slate-800/50 cursor-pointer backdrop-blur-sm">
      <div className="flex items-center gap-3 mb-3">
        <div 
          className="p-2 rounded-lg transition-all duration-300 group-hover:scale-110"
          style={{ 
            background: `${color}20`,
            color: color
          }}
        >
          {icon}
        </div>
        <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">{title}</h3>
      </div>
      <p className="text-slate-600 dark:text-slate-400 text-sm">{description}</p>
    </div>
  );
}

const contactMethods = [
  {
    icon: <Mail className="h-6 w-6" />,
    title: "Email Support",
    subtitle: "support@crediflow.ai",
    color: "#3b82f6",
    description: "Get detailed help via email. Average response time: 2-4 hours during business hours."
  },
  {
    icon: <Phone className="h-6 w-6" />,
    title: "Phone Support",
    subtitle: "+1 (555) 123-4567",
    color: "#10b981",
    description: "Speak directly with our support team. Available during business hours."
  },
  {
    icon: <MessageCircle className="h-6 w-6" />,
    title: "Live Chat",
    subtitle: "Available 24/7",
    color: "#8b5cf6",
    description: "Get instant help through our live chat system. Fastest response times."
  }
];

const features = [
  {
    icon: <Zap className="h-5 w-5" />,
    title: "Quick Start",
    description: "Get up and running in minutes",
    color: "#f59e0b"
  },
  {
    icon: <Shield className="h-5 w-5" />,
    title: "Secure Platform",
    description: "Enterprise-grade security",
    color: "#10b981"
  },
  {
    icon: <Clock className="h-5 w-5" />,
    title: "24/7 Support",
    description: "Round-the-clock assistance",
    color: "#8b5cf6"
  },
  {
    icon: <CheckCircle className="h-5 w-5" />,
    title: "Proven Results",
    description: "Trusted by thousands",
    color: "#3b82f6"
  }
];

const faqData = [
  { 
    q: "How do I upload documents for analysis?", 
    a: "Navigate to your dashboard and use the intuitive drag-and-drop area to upload your Excel files. The system supports both .xlsx and .xls formats. You can also click the upload button to browse your files manually. Files are processed securely and encrypted during transfer." 
  },
  { 
    q: "What file formats are supported?", 
    a: "Currently, Crediflow AI supports Excel files in both .xlsx and .xls formats. We're continuously working to add support for additional formats including CSV, PDF, and Google Sheets integration." 
  },
  { 
    q: "How long does report generation take?", 
    a: "Report generation typically takes 1-3 minutes depending on file size and complexity. Our advanced AI algorithms work efficiently to provide accurate analysis. A real-time progress indicator shows the current status of your report generation." 
  },
  { 
    q: "How can I view my previous reports?", 
    a: "Access all your generated reports by clicking 'Previous Reports' in your dashboard sidebar. Reports are organized chronologically and include search functionality to quickly find specific analyses. You can also filter by date range or report type." 
  },
  { 
    q: "What is the report quota system?", 
    a: "Each subscription tier includes a monthly quota of reports you can generate. Your current usage and remaining quota are always visible in the dashboard sidebar. Unused quotas don't roll over to the next month, but you can upgrade your plan anytime for more reports." 
  },
  {
    q: "Is my data secure and private?",
    a: "Absolutely. We use enterprise-grade encryption for all data transfers and storage. Your files are processed in secure, isolated environments and automatically deleted after processing. We never share or sell your data to third parties."
  },
  {
    q: "Can I export my reports?",
    a: "Yes! Reports can be exported in multiple formats including PDF, Excel, and PowerPoint. You can also share reports directly via email or generate shareable links with customizable access permissions."
  },
  {
    q: "What if I encounter technical issues?",
    a: "Our support team is available 24/7 to help with any technical issues. You can reach us via live chat, email, or phone. We also have a comprehensive troubleshooting guide and video tutorials available in the help center."
  }
];

const steps = [
  { 
    title: "Create Your Account", 
    desc: "Sign up with your email and complete the verification process to access all features.",
    tip: "Use a business email for faster verification and access to enterprise features."
  },
  { 
    title: "Upload Your Document", 
    desc: "Drag and drop your Excel file into the secure upload area or browse to select files.",
    tip: "Ensure your Excel file has clear headers and organized data for best results."
  },
  { 
    title: "Configure Analysis Settings", 
    desc: "Name your report, select analysis type, and customize settings based on your needs.",
    tip: "Detailed descriptions help you find reports later in your dashboard."
  },
  { 
    title: "Review Generated Report", 
    desc: "Wait for AI to complete the analysis and explore your comprehensive report with insights.",
    tip: "Use the interactive features to dive deeper into specific data points."
  },
];